import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { fetchJson } from "@/lib/api";

// Phase 2b coi-upload-and-review — react-query hooks for the
// customer-side upload + review flow. Mirrors the agreement-signer
// pattern: a single resource id (the coi_documents row) drives every
// call. Poll `useCoiUpload` after `useUploadCoi` finishes to watch
// status transitions through processing → awaiting_customer_review
// (or awaiting_admin_review).

export interface CoiCheck {
  status: "green" | "amber" | "red";
  required: boolean;
  label: string;
  ok: boolean;
}

export interface CoiDocument {
  id: number;
  status:
    | "processing"
    | "awaiting_customer_review"
    | "awaiting_admin_review"
    | "approved"
    | "rejected"
    | "expired"
    | "ocr_failed";
  extracted_fields: Record<string, unknown> | null;
  reviewed_fields: Record<string, unknown> | null;
  ocr_confidence_score: number | null;
  checks: Record<string, CoiCheck>;
  file_url: string | null;
  file_filename: string | null;
  updated_at: string;
}

export function coiKey(id: number): readonly unknown[] {
  return ["coi", "uploads", id];
}

export function useCoiUpload(id: number | null): UseQueryResult<CoiDocument> {
  return useQuery({
    queryKey: coiKey(id ?? 0),
    queryFn: () => fetchJson<CoiDocument>(`/api/v1/coi/uploads/${id ?? 0}`),
    enabled: id !== null && id > 0,
    // FR-1 polling — server flips `status` once OCR finishes; we want
    // the customer's screen to reflect that without manual reload.
    refetchInterval: (query) => {
      const d = query.state.data as CoiDocument | undefined;
      if (!d) return 2000;
      return d.status === "processing" ? 2000 : false;
    }
  });
}

export function useUploadCoi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<CoiDocument> => {
      const form = new FormData();
      form.append("file", file);
      const csrf =
        document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content ?? "";
      const res = await fetch("/api/v1/coi/uploads", {
        method: "POST",
        body: form,
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrf }
      });
      const text = await res.text();
      if (!res.ok) {
        throw new Error(`upload failed (${res.status}): ${text}`);
      }
      return JSON.parse(text) as CoiDocument;
    },
    onSuccess: (doc) => qc.setQueryData(coiKey(doc.id), doc)
  });
}

export interface CoiReviewBody {
  insurer_name: string;
  policy_number: string;
  effective_date: string;
  expiry_date: string;
  gl_each_occurrence_cents: number;
  gl_aggregate_cents: number;
  auto_combined_single_limit_cents?: number | null;
  property_limit_cents?: number | null;
  additional_insured_on: boolean;
  loss_payee_on: boolean;
  cancellation_notice_days?: number | null;
}

export function useSubmitCoiReview(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CoiReviewBody) =>
      fetchJson<CoiDocument>(`/api/v1/coi/uploads/${id}/submit`, {
        method: "POST",
        body: { ...body }
      }),
    onSuccess: (doc) => qc.setQueryData(coiKey(id), doc)
  });
}
